import { useCallback, useState } from "react";
import { type ReuseInfoGetAPIResponse } from "@/features/acquireBook/api/ReuseInfoAPI";
import {
  type ReuseUiData,
  type PictureTypeData,
  type AcquireBookPictures,
  type AcquireBookZenbujiko,
  type ReuseUiDetailData,
} from "@/types/acquirebook";
import { type UseFormReturn } from "react-hook-form";
import { getInitialPictureRequestOptions } from "@/utils/utilsAcquireBook";
import { setValuesRecursively } from "@/utils/utilsReactHookForms";
import { type ReusePictures } from "@/features/acquireBook/utils/pictureRequest";
import { isBefore, isEqual, parseISO } from "date-fns";

interface UsePictureCheckOptionsManagerParams {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  reactHookForm: UseFormReturn<any>; // TODO pictureCheckOptionsのみに型を制限したい
  initialDisabledReuseRequest: boolean;
  initialPictureCheckOptions: PictureTypeData;
  reuseInfoGetMutateAPIResponseList: ReuseInfoGetAPIResponse[];
  setReuseInfoGetMutateAPIResponseList: (
    value: ReuseInfoGetAPIResponse[]
  ) => void;
}

interface UsePictureCheckOptionsManagerReturn {
  disabledReuseRequest: boolean;
  getReuseData: (key: AcquireBookPictures) => ReuseUiData;
  updatePictureCheckOptionsForm: (
    realEstateTypeEng: string,
    prefectureId: string,
    location: string,
    chibanKaokuNumbers: string[],
    isAllReuseHidden: boolean,
    preserveCheckedState: boolean
  ) => void;
  getReusePictures: () => ReusePictures[];
}

/**
 * 登記図面に関連するフォーム状態を管理するカスタムフック
 */
export const usePictureCheckOptionsManager = ({
  reactHookForm,
  initialDisabledReuseRequest,
  initialPictureCheckOptions,
  reuseInfoGetMutateAPIResponseList,
  setReuseInfoGetMutateAPIResponseList,
}: UsePictureCheckOptionsManagerParams): UsePictureCheckOptionsManagerReturn => {
  // 再利用情報取得可否
  const [disabledReuseRequest, setDisabledReuseRequest] = useState<boolean>(
    initialDisabledReuseRequest
  );

  // 全ての再利用情報ラジオボタンを非表示にするフラグ
  const [isAllReuseHidden, setIsAllReuseHidden] = useState<boolean>(
    reuseInfoGetMutateAPIResponseList.length === 0
  );

  /**
   * 指定したkeyの表示する再利用データを取得する
   * - 表示しない場合は項目単位でnullを返す
   * @param key
   * @returns ReuseUiData
   */
  const getReuseData = useCallback(
    (key: AcquireBookPictures): ReuseUiData => {
      if (isAllReuseHidden) {
        return {
          reuseNew: null,
          reuseTrustart: null,
          reuseOwn: null,
        };
      }

      // 現在のキーに対応するデータを取得
      const { reuseOwn, reuseTrustart } = getReuseKeyData(
        reuseInfoGetMutateAPIResponseList,
        key,
        initialPictureCheckOptions,
        reactHookForm.watch("requestClosureBook") as boolean
      );

      // 非活性化フラグを取得
      const disabled = !initialPictureCheckOptions[key].checked;

      // 取得したデータをUIに表示する形式に変換
      const convertedReuseTrustart = convertToUiDetailData(
        reuseTrustart,
        disabled
      );
      const convertedReuseOwn = convertToUiDetailData(reuseOwn, disabled);

      if (convertedReuseTrustart && convertedReuseOwn) {
        /**
         * パターン1: reuseTrustartとreuseOwnの両方が存在する場合
         */
        if (
          compareApiData({
            reuseTrustart: convertedReuseTrustart,
            reuseOwn: convertedReuseOwn,
          })
        ) {
          /**
           * パターン1-1: APIから取得したデータが正常の場合
           */
          if (
            convertedReuseTrustart.pictureRequestDetailId ===
              convertedReuseOwn.pictureRequestDetailId &&
            isEqual(
              parseISO(convertedReuseTrustart.acquiredDate),
              parseISO(convertedReuseOwn.acquiredDate)
            )
          ) {
            /**
             * パターン1-1-1: リユース登記と取得済登記の日時が同じ場合は、リユース登記を表示しない
             * - 日時が同じ場合はpictureRequestDetailIdも同じであること
             */
            return {
              reuseNew: { disabled },
              reuseTrustart: null, // 非表示
              reuseOwn: convertedReuseOwn,
            };
          } else {
            /**
             * パターン1-1-2: 上記以外
             */
            return {
              reuseNew: { disabled },
              reuseTrustart: convertedReuseTrustart,
              reuseOwn: convertedReuseOwn,
            };
          }
        }
      } else if (convertedReuseTrustart) {
        /**
         * パターン2: reuseTrustartのみ存在する場合
         */
        return {
          reuseNew: { disabled },
          reuseTrustart: convertedReuseTrustart,
          reuseOwn: null, // 非表示
        };
      } else if (convertedReuseOwn) {
        /**
         * パターン3: reuseOwnのみ存在する場合
         */
        return {
          reuseNew: { disabled },
          reuseTrustart: null, // 非表示
          reuseOwn: convertedReuseOwn,
        };
      }

      /**
       * パターン4: 上記以外
       */
      return {
        reuseNew: { disabled },
        reuseTrustart: null, // 非表示
        reuseOwn: null, // 非表示
      };
    },
    [
      initialPictureCheckOptions,
      reuseInfoGetMutateAPIResponseList,
      isAllReuseHidden,
    ]
  );

  /**
   * フォームの状態変更時の処理
   * - 表示内容を更新するための関数
   */
  const updatePictureCheckOptionsForm = useCallback(
    (
      realEstateTypeEng: string,
      prefectureId: string,
      location: string,
      chibanKaokuNumbers: string[],
      isAllReuseHidden: boolean, // 全ての再利用情報ラジオボタンを非表示にするかどうか
      preserveCheckedState: boolean // チェック状態を保持するかどうか
    ): void => {
      // chibanKaokuNumbersが1件より多い場合は強制的に無効化する
      const isMultipleChibanKaoku = chibanKaokuNumbers.length > 1;

      // フォームフィールドのリセット
      // TODO 暫定対応：pictureCheckOptionsは複雑な構成になっており、単純にreactHookForm.setValueを使用しても更新されない
      // TODO そのため、setValuesRecursivelyを使用しているが、公式で更新する方法があれば変更する
      setValuesRecursively(
        reactHookForm,
        "pictureCheckOptions",
        getInitialPictureRequestOptions({
          realEstateTypeEng: isMultipleChibanKaoku ? "" : realEstateTypeEng,
          hasReuseData: !isAllReuseHidden, // 全ての再利用情報ラジオボタンを非表示にする場合は再利用情報を持たない
          oldPictureCheckOptions: initialPictureCheckOptions,
          preserveCheckedState,
        })
      );

      // 再利用情報の非表示
      setIsAllReuseHidden(isAllReuseHidden);

      if (isAllReuseHidden) {
        // 全ての再利用情報ラジオボタンを非表示にする場合は、レスポンスを初期化
        setReuseInfoGetMutateAPIResponseList([]);
      }

      // 再利用リクエストボタンの有効化処理
      // フォームが有効な場合のみボタンを有効化
      const isFormValid =
        realEstateTypeEng !== "" &&
        prefectureId !== "" &&
        location !== "" &&
        chibanKaokuNumbers.length === 1; // 地番または家屋番号が1件の場合は有効

      setDisabledReuseRequest(!isFormValid);
    },
    [reactHookForm, initialPictureCheckOptions]
  );

  /**
   * picture-requestのリクエストを送信する際に、再利用情報を取得するためのデータを取得
   */
  const getReusePictures = useCallback((): ReusePictures[] => {
    const reusePictures: ReusePictures[] = [];
    const pictureCheckOptionsKeys = Object.keys(initialPictureCheckOptions);
    for (const pictureCheckOptionsKey of pictureCheckOptionsKeys) {
      const typedReuseKey = pictureCheckOptionsKey as AcquireBookPictures;
      const pictureCheckOptionValue = initialPictureCheckOptions[typedReuseKey];
      if (pictureCheckOptionValue.reuse === "REUSE_NEW") {
        reusePictures.push({
          picture: typedReuseKey,
          reuseDetailId: null,
          getNewPicture: true,
        });
      } else {
        const reuseData = getReuseData(typedReuseKey);
        if (
          pictureCheckOptionValue.reuse === "REUSE_OWN" &&
          reuseData.reuseOwn
        ) {
          reusePictures.push({
            picture: typedReuseKey,
            reuseDetailId: reuseData.reuseOwn.pictureRequestDetailId,
            getNewPicture: false,
          });
        } else if (
          pictureCheckOptionValue.reuse === "REUSE_TRUSTART" &&
          reuseData.reuseTrustart
        ) {
          reusePictures.push({
            picture: typedReuseKey,
            reuseDetailId: reuseData.reuseTrustart.pictureRequestDetailId,
            getNewPicture: false,
          });
        }
      }
    }
    return reusePictures;
  }, [initialPictureCheckOptions, getReuseData]);

  return {
    disabledReuseRequest,
    getReuseData,
    updatePictureCheckOptionsForm,
    getReusePictures,
  };
};

/**
 * 再利用APIデータの必要な部分を抽出して取得
 */
interface ReuseApiDetailData {
  pictureRequestDetailId: number;
  acquiredDate: string | null;
}

interface ReuseKeyData {
  reuseOwn: ReuseApiDetailData | null;
  reuseTrustart: ReuseApiDetailData | null;
}

/**
 * APIのレスポンスから再利用キーに対応するデータを管理しやすい形式に変換して取得
 */
const getReuseKeyData = (
  apiResponseList: ReuseInfoGetAPIResponse[],
  key: AcquireBookPictures,
  requestOptions: PictureTypeData,
  requestClosureBook: boolean
): ReuseKeyData => {
  let reuseOwn: ReuseInfoGetAPIResponse | undefined;
  let reuseTrustart: ReuseInfoGetAPIResponse | undefined;

  if (key === "ZENBUJIKO") {
    // 全部事項の場合
    // チェックボックスを選択した際にそれが全部事項の内容だった場合に適切なラジオボタンのラベルが表示されるようにする
    const selectedZenbujikoOptions = new Set<AcquireBookZenbujiko>();
    if (
      requestOptions.ZENBUJIKO.option.SHINTAKUMOKUROKU.checked &&
      !requestOptions.ZENBUJIKO.option.GENZAIJIKO.checked
    ) {
      selectedZenbujikoOptions.add("SHINTAKUMOKUROKU");
    }
    if (
      requestOptions.ZENBUJIKO.option.KYODOTANPOMOKUROKU.checked &&
      !requestOptions.ZENBUJIKO.option.GENZAIJIKO.checked
    ) {
      selectedZenbujikoOptions.add("KYODOTANPOMOKUROKU");
    }
    if (
      requestOptions.ZENBUJIKO.option.SHINTAKUMOKUROKU.checked &&
      requestOptions.ZENBUJIKO.option.GENZAIJIKO.checked
    ) {
      selectedZenbujikoOptions.add("SHINTAKUMOKUROKU_GENZAI");
    }
    if (
      requestOptions.ZENBUJIKO.option.KYODOTANPOMOKUROKU.checked &&
      requestOptions.ZENBUJIKO.option.GENZAIJIKO.checked
    ) {
      selectedZenbujikoOptions.add("KYODOTANPOMOKUROKU_GENZAI");
    }
    if (requestClosureBook) {
      selectedZenbujikoOptions.add("HEISATOUKIBO");
    }

    const matchesZenbujikoOptions = (
      reuse: ReuseInfoGetAPIResponse
    ): boolean => {
      const reuseOptions = new Set(reuse.zenbujikoOptions ?? []);
      return (
        selectedZenbujikoOptions.size === reuseOptions.size &&
        [...selectedZenbujikoOptions].every((opt) => reuseOptions.has(opt))
      );
    };

    // REUSE_OWNとREUSE_TRUSTARTのデータを探す
    reuseOwn = apiResponseList.find(
      (item) =>
        item.type === "ZENBUJIKO" &&
        item.reuseOption === "REUSE_OWN" &&
        matchesZenbujikoOptions(item)
    );
    reuseTrustart = apiResponseList.find(
      (item) =>
        item.type === "ZENBUJIKO" &&
        item.reuseOption === "REUSE_TRUSTART" &&
        matchesZenbujikoOptions(item)
    );
  } else {
    // 現在のキーに対応するデータをフィルタリング
    const filteredData = apiResponseList.filter((item) => item.type === key);

    // REUSE_OWNとREUSE_TRUSTARTのデータを探す
    reuseOwn = filteredData.find((item) => item.reuseOption === "REUSE_OWN");
    reuseTrustart = filteredData.find(
      (item) => item.reuseOption === "REUSE_TRUSTART"
    );
  }

  const reuseOwnKeyData = reuseOwn
    ? {
        pictureRequestDetailId: reuseOwn.pictureRequestDetailId,
        acquiredDate: reuseOwn.acquiredDateReuseOwn, // reuseOwnがnullでない場合はnullではない
      }
    : null;

  const reuseTrustartKeyData = reuseTrustart
    ? {
        pictureRequestDetailId: reuseTrustart.pictureRequestDetailId,
        acquiredDate: reuseTrustart.acquiredDateReuseTrustart, // reuseTrustartがnullでない場合はnullではない
      }
    : null;

  return {
    reuseOwn: reuseOwnKeyData,
    reuseTrustart: reuseTrustartKeyData,
  };
};

/**
 * UIに表示する際に日付などデータがnullの場合は扱いづらいので変換する処理
 * - 取得日付がnullになる場合は、データがおかしいので表示しない
 */
const convertToUiDetailData = (
  data: ReuseApiDetailData | null,
  disabled: boolean
): ReuseUiDetailData | null => {
  if (!data) {
    return null;
  }
  return data.acquiredDate === null // 本来であればnullはあり得ないが、取得した場合は表示しない
    ? null
    : {
        pictureRequestDetailId: data.pictureRequestDetailId,
        acquiredDate: data.acquiredDate,
        disabled,
      };
};

/**
 * リユース登記と取得済登記のデータを比較して、表示するかどうかを判断する
 *
 * 下記、データがおかしいためfalseを返す
 * - リユース登記が取得済登記より古い場合
 * - pictureRequestDetailIdが同じだが、取得日付が異なる場合
 * @param reuseTrustart
 * @param reuseOwn
 * @returns boolean false: 表示しない
 */
const compareApiData = ({
  reuseTrustart,
  reuseOwn,
}: {
  reuseTrustart: ReuseUiDetailData;
  reuseOwn: ReuseUiDetailData;
}): boolean => {
  const trustartDate = parseISO(reuseTrustart.acquiredDate);
  const ownDate = parseISO(reuseOwn.acquiredDate);

  // リユース登記が取得済登記より古い場合
  const isTrustartOld = isBefore(trustartDate, ownDate);

  // pictureRequestDetailIdが同じだが、取得日付が異なる場合
  const isDifferentDate =
    reuseTrustart.pictureRequestDetailId === reuseOwn.pictureRequestDetailId &&
    !isEqual(trustartDate, ownDate);

  // 上記の条件に当てはまる場合は表示しない
  return !(isTrustartOld || isDifferentDate);
};
